import { fromSpecific, TypedNode } from '@rehaje/core'
import {
  createCompileCode,
  transform,
  TransformFailedResult
} from '@rehaje/javascript'
import { reactive } from '@rehaje/reactive'
import {
  codeFrame,
  fastHash,
  LRUCache,
  nextTick,
  noop,
  refactor,
  Storage
} from '@rehaje/shared'

import {
  LintJavaScriptCompileError,
  LintJavaScriptRuntimeError,
  LintJavaScriptSyntaxError
} from './errors'
import { createCompiledFunctionByRunner, loadImports } from './utils'

import type { JavaScriptPlugin } from './plugin'
import type {
  JavaScriptSnapshotSpecific,
  JavaScriptSpecific,
  UniqueDependencyNode
} from './types'
import type { Dependency, ErrorConstructor, ViewScope } from '@rehaje/core'
import type { LintError } from '@rehaje/errors'
import type {
  CreateCompileCodeOptions,
  TransformOptions,
  TransformResult
} from '@rehaje/javascript'
import type { TRecord } from '@rehaje/shared'
import type { Program } from 'acorn'

export class JavaScriptNode extends TypedNode {
  static __r_lru_cache = new LRUCache(200, async (addedKeys, removedKeys) => {
    const setMany = addedKeys
      .filter((key) => typeof key === 'string' && !key.startsWith('__'))
      .map((key) => [key, this.__r_lru_cache.get(key)] as [IDBValidKey, any])
    const delMany = removedKeys
      .filter((key) => typeof key === 'string' && !key.startsWith('__'))
      .map((key) => key)
    if (setMany.length > 0) await this.__r_storage.setMany(setMany)
    if (delMany.length > 0) await this.__r_storage.delMany(delMany)
  })

  static __r_storage = new Storage('javascript')
  static __r_storage_loaded = false
  static __r_storage_promise: Promise<void> | undefined
  __r_dependency_uniqueid_maps: TRecord = {}

  __r_enable_error_capture = this.plugin.props.enableErrorCapture

  __r_enable_refactor = this.plugin.props.enableRefactor
  __r_handler = this.plugin.props.getProxyHandler?.(this)

  __r_imports = this.plugin.props.imports ?? {}

  @reactive('ref', { observe: false })
  __r_imports_loaded = true

  __r_internal_load_all_module_imports?: Promise<TRecord>

  __r_internal_require?: (module: string) => object

  @fromSpecific()
  @reactive()
  async?: boolean

  @fromSpecific()
  @reactive()
  codeRaw?: string

  @fromSpecific()
  @reactive()
  iife?: boolean

  @fromSpecific()
  @reactive()
  jsx?: boolean

  @fromSpecific()
  @reactive()
  module?: boolean

  @fromSpecific()
  @reactive()
  runner?: Function

  @reactive('computed')
  get __r_compiled_code() {
    const { ast, code } = this.__r_transformed_result
    return JavaScriptNode.__r_createCompileCodeWithCache(code, ast, {
      async: this.async,
      iife: this.iife,
      jsx: this.jsx,
      module: this.module,
      with: '__variables__'
    })
  }

  @reactive('computed')
  get __r_compiled_function() {
    try {
      const result = this.runner
        ? createCompiledFunctionByRunner(this.runner)
        : this.module
          ? new Function(
              'module',
              'require',
              '__variables__',
              this.__r_compiled_code
            )
          : new Function('__variables__', this.__r_compiled_code)
      this.fix(LintJavaScriptCompileError)
      return result
    } catch (error) {
      if (error instanceof Error) {
        this.raise(new LintJavaScriptCompileError(error))
      }
      return noop
    }
  }

  @reactive('computed')
  get __r_dependencies(): Dependency[] {
    const self = this
    return this.__r_dependency_nodes.map<Dependency>((dep) => ({
      invokedMethod: dep.identifier.invokedMethod,
      name: dep.id,
      get node() {
        return dep.id === 'self' ? self.__r_entity_parent : self.find(dep.id)
      },
      get path() {
        return [dep.id].concat(dep.identifier.subchain?.match(/\w+/g) ?? [])
      },
      relation: dep.identifier.invoked ? 'INVOCATION' : 'DEPENDENCY'
    }))
  }

  @reactive('computed')
  get __r_dependency_nodes(): UniqueDependencyNode[] {
    const nodes: UniqueDependencyNode[] = []
    this.globalVariables.forEach((identifier) => {
      const name = identifier.name
      const uniqueID = this.__r_dependency_uniqueid_maps[name]
      const node = this.find(uniqueID) ?? this.find(name)
      if (node) {
        const id = node.id
        const uniqueID = node.getUniqueID()
        nodes.push({ id, identifier, uniqueID })
        this.__r_dependency_uniqueid_maps[name] = uniqueID
      }
    })
    return nodes
  }

  get __r_require() {
    this.__r_internal_require ??= (name: string) => {
      let module = this.__r_imports[name]
      if (typeof module === 'function') module = module()
      if (module instanceof Error) throw module
      if (module instanceof Promise) {
        throw new TypeError(`Module not resolved: Can't load '${name}'`)
      }
      if (typeof module !== 'object') {
        throw new TypeError(`Module not found: Can't resolve '${name}'`)
      }
      return module
    }
    return this.__r_internal_require
  }

  @reactive('computed')
  get __r_transformed_result(): TransformResult {
    try {
      const result = JavaScriptNode.__r_transformWithCache(this.codeRaw ?? '', {
        classNamePrefix: this.plugin.props.classNamePrefix,
        extractClassNames: true,
        extractGlobalVariables: true,
        extractImportModules: true,
        jsx: this.jsx,
        loopGuard: true,
        module: this.module
      })
      this.fix(LintJavaScriptSyntaxError)
      return result
    } catch (error) {
      if (error instanceof Error) {
        this.raise(new LintJavaScriptSyntaxError(error))
      }
      return TransformFailedResult
    }
  }

  get classNames() {
    return this.__r_transformed_result.classNames ?? []
  }

  get error() {
    return (
      super.getLintError(LintJavaScriptSyntaxError) ??
      super.getLintError(LintJavaScriptCompileError) ??
      super.getLintError(LintJavaScriptRuntimeError)
    )
  }

  get globalVariables() {
    return this.__r_transformed_result.globalVariables ?? []
  }

  get importModules() {
    return (
      this.__r_transformed_result.importModules
        ?.map((module) =>
          typeof module.value === 'string' ? module.value : ''
        )
        .filter(Boolean) ?? []
    )
  }

  get loadAllModuleImports() {
    this.__r_internal_load_all_module_imports ??= new Promise((resolve) => {
      const loadResult = loadImports(this.__r_imports)
      if (loadResult.hasPromise) loadResult.imports.then(resolve)
      else resolve(loadResult.imports)
    })
    return this.__r_internal_load_all_module_imports
  }

  get plugin() {
    return this.getPlugin<JavaScriptPlugin>()
  }

  @reactive('computed')
  get useModuleImports() {
    const code = this.codeRaw
    const loadModules = this.importModules
    const loadLiterals = this.__r_transformed_result.importModules
    const loadResult = loadImports(
      this.__r_imports,
      loadModules,
      (imports, name) => {
        const literal = loadLiterals?.find((module) => module.value === name)
        if (literal) {
          const err = new Error(`Module not found: Can't resolve '${name}'`)
          const loc = literal.loc
          if (code && loc) {
            const frame = codeFrame(code, loc.start.line, loc.start.column)
            err.message = `${err.message}\n\n${frame}\n`
          }
          imports[name] = err
        }
      }
    )
    this.__r_imports_loaded = !loadResult.hasPromise
    return () => {
      if (this.__r_imports_loaded || !loadResult.hasPromise) return
      throw loadResult.imports.then(() => {
        this.__r_imports_loaded = true
      })
    }
  }

  constructor(specific: JavaScriptSpecific) {
    super(specific)
    this.startAutoRefactor(specific)
  }

  static __r_createCompileCodeWithCache(
    code: string,
    codeAst: Program,
    options: CreateCompileCodeOptions
  ) {
    const key = fastHash(`//${JSON.stringify(options)}\n${code}`)
    let result = this.__r_lru_cache.get<string>(key)
    if (result) return result
    result = createCompileCode(code, codeAst, options)
    this.__r_lru_cache.set(key, result)
    return result
  }

  static __r_transformWithCache(code: string, options: TransformOptions) {
    const key = fastHash(`//${JSON.stringify(options)}\n${code}`)
    let result = this.__r_lru_cache.get<TransformResult>(key)
    if (result) return result
    result = transform(code, options)
    this.__r_lru_cache.set(key, result)
    return result
  }

  /**
   * JavaScriptNode 编译产物缓存
   * 适合 react 19+ 搭配 use Hook 使用
   *
   * @example
   * const App: FC = () => {
   *   use(JavaScriptNode.loadJavaScriptStorage())
   *   const store = useMemo(() =>
   *     createStore(dsl, {
   *       plugins: [new JavaScriptPlugin()]
   *     })
   *   )
   * }
   */
  static async loadJavaScriptStorage() {
    if (this.__r_storage_loaded) return
    const entries = await this.__r_storage.entries()
    entries.forEach(([key, value]) => {
      if (typeof key === 'string') {
        this.__r_lru_cache.set(key, value, false)
      }
    })
    this.__r_storage_loaded = true
  }

  static lruGet<T = any>(key: string): T | undefined {
    return this.__r_lru_cache.get(key)
  }

  static lruSet<T = any>(key: string, value: T) {
    this.__r_lru_cache.set(key, value)
  }

  /**
   * JavaScriptNode 编译产物缓存
   * 适合 react 16-18 搭配 Suspense 使用
   *
   * @example
   * const App: FC = () => {
   *   JavaScriptNode.useJavaScriptStorage()
   *   const store = useMemo(() =>
   *     createStore(dsl, {
   *       plugins: [new JavaScriptPlugin()]
   *     })
   *   )
   * }
   */
  static useJavaScriptStorage() {
    if (this.__r_storage_loaded) return
    this.__r_storage_promise ??= this.loadJavaScriptStorage()
    throw this.__r_storage_promise
  }

  __r_run(scope: ViewScope) {
    try {
      const variables = this.runner
        ? this.__r_handler
          ? new Proxy(scope.variables, this.__r_handler)
          : scope.variables
        : this.__r_handler
          ? new Proxy(scope.dependencies, this.__r_handler)
          : scope.dependencies
      const result =
        this.runner || !this.module
          ? this.__r_compiled_function(variables)
          : this.__r_imports_loaded
            ? this.__r_compiled_function({}, this.__r_require, variables)
            : noop
      this.fix(LintJavaScriptRuntimeError)
      return result
    } catch (error) {
      if (error instanceof Error) {
        this.raise(new LintJavaScriptRuntimeError(error))
      }
    }
  }

  override fix<T extends LintError>(
    constructor: ErrorConstructor<T>
  ): T | undefined {
    if (this.__r_enable_error_capture) return super.fix(constructor)
  }

  override getDependencies(): Dependency[] {
    return this.__r_dependencies
  }

  override getDependencyVariables(): string[] {
    return this.globalVariables.map((variable) => variable.name)
  }

  override raise(error: Error) {
    if (this.__r_enable_error_capture) return super.raise(error)
  }

  startAutoRefactor(specific: Partial<JavaScriptSnapshotSpecific>): void {
    if (specific.dependencies) {
      this.addEventListener(
        'onFromSnapshot',
        (event) => {
          const relation = event.detail
          const dependencies = specific.dependencies
          this.codeRaw = refactor(
            this.codeRaw,
            this.__r_dependency_nodes.map((node) => {
              const snapshotID = dependencies?.find(
                (dep) => dep.id === node.id
              )?.snapshotID
              const relationID = snapshotID
                ? relation?.[snapshotID]?.id
                : undefined
              return { ...node.identifier, name: relationID ?? node.id }
            })
          )
        },
        { once: true }
      )
    }
    if (this.__r_enable_refactor) {
      nextTick(() => {
        this.setReaction(
          'autoRefactor',
          () => this.__r_dependency_nodes,
          (nodes) => {
            this.codeRaw = refactor(
              this.codeRaw,
              nodes
                .filter((node) => node.id !== node.identifier.name)
                .map((node) => ({ ...node.identifier, name: node.id }))
            )
          },
          {
            equals: (nodes) =>
              nodes.every((node) => node.id === node.identifier.name)
          }
        )
      })
    }
  }

  override toSnapshot(): JavaScriptSnapshotSpecific {
    return {
      ...super.toSnapshot(),
      dependencies: this.__r_dependency_nodes.map(({ id, identifier }) => ({
        id,
        identifier,
        snapshotID: this.find(id)?.getSnapshotID()
      }))
    } as JavaScriptSnapshotSpecific
  }

  override transpile(scope: ViewScope, justCompute?: boolean) {
    if (justCompute) return this.__r_run(scope)

    let result
    scope.setReaction(
      'expression',
      () => (result = this.__r_run(scope)),
      (value) => scope.parent?.model?.setInProperty(this.getBelongs(), value),
      { onError: (error) => this.raise(error) }
    )

    return result
  }
}

declare global {
  export interface RehajeSpecifics {
    JavaScript: JavaScriptSpecific
  }
  export interface RehajeTypedNodes {
    JavaScript: JavaScriptNode
  }
}
